import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Database, CalendarBlank, Clock, Users, ChartBar, Wine, CaretDown, CaretUp, DownloadSimple, FilePdf } from "@phosphor-icons/react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const generatePdfReport = async (type) => {
  try {
    let data, title, headers, rows;
    const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

    if (type === "daily") {
      const res = await api.get("/export/daily-report?format=json");
      data = res.data;
      title = "Daily Report";
      headers = ["Guest Name", "Member #", "Party Size", "Time", "Type", "Status", "Notes"];
      rows = data.map((r) => [r.guest_name, r.member_number || "-", r.party_size, r.time, r.type || "reservation", r.status, r.notes || "-"]);
    } else if (type === "members") {
      const res = await api.get("/export/members?format=json");
      data = res.data;
      title = "Members Report";
      headers = ["Name", "Member #", "Phone", "Email", "Visits", "Last Visit", "Allergens"];
      rows = data.map((m) => [m.name, m.member_number || "-", m.phone || "-", m.email || "-", m.visit_count || 0, m.last_visit ? m.last_visit.split("T")[0] : "-", m.allergens || "-"]);
    } else {
      const res = await api.get("/export/server-performance?format=json");
      data = res.data;
      title = "Server Performance";
      headers = ["Server", "Tables", "Covers", "Turns"];
      rows = data.map((s) => [s.server_name, s.tables, s.covers, s.turns]);
    }

    const tableRows = rows.map((r) =>
      `<tr>${r.map((c) => `<td style="padding:6px 10px;border-bottom:1px solid #E7E5E4;font-size:12px;">${c}</td>`).join("")}</tr>`
    ).join("");

    const html = `<html><head><title>${title} - ${today}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; max-width: 800px; margin: 30px auto; color: #1C1917; padding: 20px; }
        h1 { font-size: 22px; color: #2F4F4F; margin-bottom: 4px; }
        .subtitle { color: #78716C; font-size: 13px; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; padding: 8px 10px; font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; color: #78716C; border-bottom: 2px solid #2F4F4F; font-weight: 600; }
        .summary { font-size: 13px; color: #78716C; margin-bottom: 16px; }
        .footer { margin-top: 24px; text-align: center; font-size: 10px; color: #A8A29E; }
        @media print { body { margin: 10px; } }
      </style></head><body>
      <h1>Dining Floor Studio</h1>
      <p class="subtitle">${title} — ${today}</p>
      <p class="summary">${rows.length} record${rows.length !== 1 ? "s" : ""}</p>
      <table><thead><tr>${headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${tableRows}</tbody></table>
      <div class="footer">Generated by Dining Floor Studio · Created by Jordan Thomas · © 2026</div>
      </body></html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  } catch { /* silent */ }
};

export default function DataPage() {
  const { restId } = useRestaurant();
  const [serviceHistory, setServiceHistory] = useState([]);
  const [resHistory, setResHistory] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [expandedSession, setExpandedSession] = useState(null);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "?";
      const [sh, rh, ev] = await Promise.all([
        api.get(`/data/service-history${params}&limit=30`),
        api.get(`/data/reservation-history${params}&days=${days}`),
        api.get(`/data/event-log${params}&limit=50`),
      ]);
      setServiceHistory(sh.data);
      setResHistory(rh.data);
      setPastEvents(ev.data);
    } catch { toast.error("Failed to load data"); }
  }, [restId, days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const completedSessions = serviceHistory.filter((s) => s.state === "completed" || s.state === "post_service");

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="data-page">
      <div className="flex items-center gap-2 mb-6">
        <Database size={24} weight="duotone" style={{ color: "#2F4F4F" }} />
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Data & Logistics</h2>
        <Badge className="bg-red-100 text-red-700 text-[10px]">Admin Only</Badge>
      </div>

      {/* Export Buttons */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-5" data-testid="export-section">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2">
          <FilePdf size={16} weight="duotone" /> Export Reports (PDF)
        </h3>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="text-xs" onClick={() => generatePdfReport("daily")} data-testid="export-daily-btn">
            <FilePdf size={14} className="mr-1.5" /> Daily Report
          </Button>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => generatePdfReport("members")} data-testid="export-members-btn">
            <FilePdf size={14} className="mr-1.5" /> Members
          </Button>
          <Button variant="outline" size="sm" className="text-xs" onClick={() => generatePdfReport("server")} data-testid="export-server-perf-btn">
            <FilePdf size={14} className="mr-1.5" /> Server Performance
          </Button>
        </div>
      </div>

      {/* Reservation History Chart */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-5" data-testid="res-history-chart">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 flex items-center gap-2">
            <CalendarBlank size={16} weight="duotone" /> Reservation History
          </h3>
          <div className="flex gap-1">
            {[7, 14, 30].map((d) => (
              <button key={d} className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all ${days === d ? "bg-[#2F4F4F] text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`} onClick={() => setDays(d)} data-testid={`days-${d}-btn`}>
                {d}d
              </button>
            ))}
          </div>
        </div>
        {resHistory.length === 0 ? (
          <p className="text-sm text-stone-500 text-center py-6">No reservation data for this period</p>
        ) : (
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={resHistory.slice().reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "#57534E" }} interval={Math.max(0, Math.floor(resHistory.length / 8))} />
                <YAxis tick={{ fontSize: 11, fill: "#57534E" }} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #E7E5E4", fontSize: "12px" }} />
                <Bar dataKey="completed" fill="#81B29A" name="Completed" radius={[2, 2, 0, 0]} stackId="a" />
                <Bar dataKey="cancelled" fill="#E07A5F" name="Cancelled" radius={[2, 2, 0, 0]} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {/* Summary Row */}
        {resHistory.length > 0 && (
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-stone-100 text-xs text-stone-500">
            <span>Total: <strong className="text-stone-800">{resHistory.reduce((a, r) => a + r.total, 0)}</strong></span>
            <span>Completed: <strong className="text-green-700">{resHistory.reduce((a, r) => a + r.completed, 0)}</strong></span>
            <span>Cancelled: <strong className="text-red-600">{resHistory.reduce((a, r) => a + r.cancelled, 0)}</strong></span>
            <span>Total Covers: <strong className="text-stone-800">{resHistory.reduce((a, r) => a + r.covers, 0)}</strong></span>
          </div>
        )}
      </div>

      {/* Service Session History */}
      <div className="bg-white rounded-lg border border-stone-200 p-5 mb-5" data-testid="service-history">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
          <Clock size={16} weight="duotone" /> Service Session History
        </h3>
        {completedSessions.length === 0 ? (
          <p className="text-sm text-stone-500 text-center py-6">No past service sessions</p>
        ) : (
          <div className="space-y-2">
            {completedSessions.map((s) => {
              const summary = s.summary || {};
              const isExpanded = expandedSession === s.id;
              return (
                <div key={s.id} className="border border-stone-100 rounded-lg overflow-hidden" data-testid={`session-${s.id}`}>
                  <button onClick={() => setExpandedSession(isExpanded ? null : s.id)} className="w-full flex items-center justify-between p-3 hover:bg-stone-50 transition-colors text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#2F4F4F]/10 flex items-center justify-center">
                        <Clock size={14} style={{ color: "#2F4F4F" }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-800">
                          {s.started_at ? new Date(s.started_at).toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) : "Session"}
                        </p>
                        <p className="text-[10px] text-stone-500">
                          {s.started_at ? new Date(s.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""} 
                          {s.ended_at ? ` - ${new Date(s.ended_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {summary.total_covers > 0 && <span className="text-xs text-stone-500"><strong>{summary.total_covers}</strong> covers</span>}
                      {summary.total_reservations > 0 && <span className="text-xs text-stone-500"><strong>{summary.total_reservations}</strong> res</span>}
                      {isExpanded ? <CaretUp size={14} className="text-stone-500" /> : <CaretDown size={14} className="text-stone-500" />}
                    </div>
                  </button>
                  {isExpanded && summary && (
                    <div className="px-3 pb-3 pt-1 bg-stone-50 border-t border-stone-100">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="text-center p-2 rounded-lg bg-white">
                          <p className="text-[10px] text-stone-500 uppercase">Duration</p>
                          <p className="text-sm font-bold" style={{ color: "#2F4F4F" }}>{summary.service_duration_minutes}m</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-white">
                          <p className="text-[10px] text-stone-500 uppercase">Covers</p>
                          <p className="text-sm font-bold text-stone-800">{summary.total_covers}</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-white">
                          <p className="text-[10px] text-stone-500 uppercase">Avg Turn</p>
                          <p className="text-sm font-bold" style={{ color: "#E07A5F" }}>{summary.avg_turn_minutes > 0 ? `${summary.avg_turn_minutes}m` : "--"}</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-white">
                          <p className="text-[10px] text-stone-500 uppercase">Turns</p>
                          <p className="text-sm font-bold text-stone-800">{summary.total_turns}</p>
                        </div>
                      </div>
                      <div className="flex gap-3 mt-2 text-xs text-stone-500">
                        <span>Completed: {summary.completed}</span>
                        <span>Seated: {summary.seated}</span>
                        <span>Cancelled: {summary.cancelled}</span>
                        <span>Walk-ins: {summary.walk_ins_seated}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Past Events Archive */}
      <div className="bg-white rounded-lg border border-stone-200 p-5" data-testid="past-events">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-4 flex items-center gap-2">
          <Wine size={16} weight="duotone" /> Event Archive
        </h3>
        {pastEvents.length === 0 ? (
          <p className="text-sm text-stone-500 text-center py-6">No events recorded</p>
        ) : (
          <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
            {pastEvents.map((ev) => (
              <div key={ev.id} className="flex items-center justify-between p-2.5 rounded-lg border border-stone-100 text-xs" data-testid={`archive-event-${ev.id}`}>
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="font-medium text-stone-700 truncate">{ev.name}</span>
                  <span className="text-stone-500">{ev.event_type}</span>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span className="text-stone-500">{ev.date}</span>
                  <span className="text-stone-500">{ev.guest_count} guests</span>
                  <Badge className={`text-[9px] ${ev.status === "completed" ? "bg-green-100 text-green-700" : ev.status === "cancelled" ? "bg-red-100 text-red-600" : "bg-stone-100 text-stone-500"}`}>
                    {ev.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
